import React, { useEffect, useMemo, useState } from 'react';
import ListIcon from '@material-ui/icons/List';
import GetAppIcon from '@material-ui/icons/GetApp';
import { LoaderBar, Page } from 'src/components';
import PerfectScrollbar from 'react-perfect-scrollbar';
import ViewWeekIcon from '@material-ui/icons/ViewWeek';
import {
  Box,
  Card,
  Table,
  makeStyles,
  Container,
  Button,
  Grid,
  Typography
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { usePermissions, useSnackBar } from 'src/hooks';
import InventoryToolbar from './component/InventoryToolbar';
import InventoryFilter from './component/InventoryFilter';
import {
  InventoryColumnsFilterModal,
  InventoryLocalFilterModal,
  InventoryTableHeader
} from './component';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { InventoryTableBody } from './component/InventoryTableBody';
import { Inventory, InventoryColumn, ManufacturerOption } from 'src/types';
import useUserInfo from 'src/hooks/user/use-user-info';
import { useBranchInfo } from 'src/hooks/branch/use-branch-info';
import { Alert, Pagination } from '@material-ui/lab';
import { isEmpty } from 'lodash';
import { unwrapResult } from '@reduxjs/toolkit';
import { downloadCSV } from 'src/utils';

const { actions: manufacturerActions } = slices.manufacturer;

const {
  actions: inventoryActions,
  selectors: inventorySelectors
} = slices.inventory;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  columnBtn: {
    marginTop: theme.spacing(2),
    justifyContent: 'space-between',
    display: 'flex'
  },
  toCsvText: {
    color: theme.palette.primary.main
  },
  toCsvBtn: { marginLeft: theme.spacing(2) }
}));

const InventoryView = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const snackBar = useSnackBar();
  const inventoryResponse = useAppSelector(
    inventorySelectors.selectInventoryResponse
  );
  const inventoryColumns = useAppSelector(
    inventorySelectors.selectInventoryColumns
  );
  const items = useAppSelector(inventorySelectors.selectInventoryResponseItems);
  const meta = useAppSelector(inventorySelectors.selectMeta);
  const isSummaryFetched = useAppSelector(
    inventorySelectors.selectIsSummaryFetched
  );
  const status = useAppSelector(inventorySelectors.selectStatus);
  const permissions = usePermissions();
  const { getUserDetails, userBranchOptions, allBranchIDs } = useUserInfo();
  const { selectedBranchIDs, setSelectedBranchIDs } = useBranchInfo();

  const [manufacturers, setManufacturers] = useState<ManufacturerOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filterVisible, setFilterVisible] = useState<boolean>(false);
  const [selectedColumnFilter, setSelectedColumnFilter] = useState<
    InventoryColumn
  >();

  const hasStatus = useMemo(() => {
    return !!status && !!status[0];
  }, [status]);

  const handleSetManufacturer = (id?: number) => {
    if (id) {
      dispatch(
        inventoryActions.updateManufacturerFilter({
          manufacturer_id: id
        })
      );
    } else {
      dispatch(
        inventoryActions.updateManufacturerFilter({ manufacturer_id: null })
      );
    }
  };

  const handleExport = async () => {
    // any as bandaid solution to ts error
    const res: any = unwrapResult(
      await dispatch(inventoryActions.getInventoryForCSVThunk())
    );
    if (res.success) {
      const formattedData = res.originalData.items.map((item: Inventory) => ({
        id: inventoryColumns.id ? item?.id : null,
        branch_id: item?.branch_id,
        ec_order_id: null,
        category_id: item?.category_id,
        category_name: item?.category_name,
        po_no: item?.po_no,
        quantity: inventoryColumns.quantity ? item?.quantity : null,
        product_name: inventoryColumns.product_name ? item?.product_name : null,
        sku: inventoryColumns.sku ? item?.sku : null,
        dr_no: item?.dr_no,
        serial_no: inventoryColumns.serial_no ? item?.serial_no : null,
        status: inventoryColumns.status ? item?.status : null,
        dealers_price: inventoryColumns.dealers_price
          ? item?.dealers_price
          : null,
        retail_price: inventoryColumns.retail_price ? item?.retail_price : null,
        manufacturer_id: inventoryColumns.manufacturer_id
          ? item?.manufacturer_id
          : null,
        supplier_name: inventoryColumns.supplier_name
          ? item?.supplier_name
          : null,
        branch_name: inventoryColumns.branch_name ? item?.branch_name : null,
        encoded_by: inventoryColumns.encoded_by ? item?.encoded_by : null,
        released_by: inventoryColumns.released_by ? item?.released_by : null,
        transaction_no: inventoryColumns.transaction_no
          ? item?.transaction_no
          : null,
        customer_name: inventoryColumns.customer_name
          ? item?.customer_name
          : null,
        date_sold: inventoryColumns.date_sold ? item?.date_sold : null,
        date_purchased: inventoryColumns.date_purchased
          ? item?.date_purchased
          : null,
        date_encoded: inventoryColumns.date_encoded ? item?.date_encoded : null
      }));

      const filteredFormattedData = formattedData.map((row: any) => {
        return Object.fromEntries(
          Object.entries(row).filter(
            ([key, value]) => key === 'dr_no' || value !== null
          )
        );
      });

      downloadCSV(filteredFormattedData, 'inventory');
    }
  };

  const getManufacturers = async (keyword?: string) => {
    const getManufacturersResponse = unwrapResult(
      await dispatch(
        manufacturerActions.getManufacturersThunk({ keyword, limit: 9999 })
      )
    );
    if (getManufacturersResponse?.success) {
      const manufacturers = getManufacturersResponse?.originalData?.data;
      let manufacturerOptions = manufacturers?.map((i) => ({
        label: i.name || '',
        id: i.id ?? 0
      }));
      setManufacturers(manufacturerOptions || []);
      return;
    } else {
      console.error('Failed to get Manufacturers');
      return;
    }
  };

  const onHandlePageChange = (event: any, page: number) => {
    setIsLoading(true);
    dispatch(inventoryActions.updatePageFilter({ page: page }));
    dispatch(inventoryActions.getInventoryThunk()).finally(() =>
      setIsLoading(false)
    );
  };

  const handleBranchChange = (v: number[]) => {
    if (v[0]) {
      setSelectedBranchIDs(v);

      dispatch(
        inventoryActions.updateBranchFilter({
          branch_ids: v
        })
      );
      //   set all if no value has no items
    } else {
      setSelectedBranchIDs(allBranchIDs);

      dispatch(
        inventoryActions.updateBranchFilter({
          branch_ids: allBranchIDs
        })
      );
    }
  };

  const getInventory = async (val: string) => {
    setIsLoading(true);
    dispatch(inventoryActions.updatePageFilter({ page: 1 }));
    dispatch(inventoryActions.updateActionFilter({ action: val }));
    dispatch(inventoryActions.getInventoryThunk()).finally(() =>
      setIsLoading(false)
    );
  };

  const clearColumnsFilter = () => {
    dispatch(inventoryActions.clearLocalFilter());
  };

  const onColumnFilterClick = (column: InventoryColumn) => {
    setSelectedColumnFilter(column);
  };

  useEffect(() => {
    if (allBranchIDs[0]) {
      handleBranchChange(allBranchIDs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBranchIDs]);

  useEffect(() => {
    getManufacturers();
    getUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (inventoryResponse?.success) {
      snackBar.show({
        severity: 'success',
        message: inventoryResponse?.message
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryResponse]);

  return (
    <Page className={classes.root} title="Inventory">
      <Container maxWidth={false}>
        <InventoryFilter
          hasStatus={hasStatus}
          userBranchOptions={userBranchOptions}
          manufacturers={manufacturers}
          isLoading={isLoading}
          selectedBranchIDs={selectedBranchIDs}
          onApplyFilterPress={getInventory}
          handleBranchChange={handleBranchChange}
          handleSetManufacturer={handleSetManufacturer}
        />
        <Grid>
          {!isEmpty(inventoryResponse?.errors) ? (
            <Alert severity="error">
              {Object.values(inventoryResponse?.errors || {}).map(
                (errorMessage, index) => (
                  <Typography key={index} variant="subtitle2">
                    {'-' + errorMessage}
                  </Typography>
                )
              )}
            </Alert>
          ) : null}
          {!hasStatus && (
            <Alert severity="error">
              <Typography variant="subtitle2">
                Please select atleast one status
              </Typography>
            </Alert>
          )}
        </Grid>
        {permissions?.canViewInventoryTotal ? <InventoryToolbar /> : null}
        <div className={classes.columnBtn}>
          {isSummaryFetched && (
            <Button
              disabled={!hasStatus || !!items?.[0]}
              variant="outlined"
              endIcon={<ListIcon />}
              onClick={() => getInventory('list')}
              color="primary"
            >
              Show List
            </Button>
          )}
          <Box>
            <Button
              variant="outlined"
              endIcon={<ClearIcon />}
              onClick={clearColumnsFilter}
              color="secondary"
            >
              Clear Columns Filter
            </Button>
            <Button
              className={classes.toCsvBtn}
              variant="outlined"
              endIcon={<ViewWeekIcon />}
              onClick={() => setFilterVisible(!filterVisible)}
              color="primary"
            >
              Columns
            </Button>
            {items?.length ? (
              <Button
                className={classes.toCsvBtn}
                variant="outlined"
                endIcon={<GetAppIcon />}
                color="primary"
                onClick={handleExport}
              >
                Export to CSV
              </Button>
            ) : null}
          </Box>
        </div>
        <Box mt={2}>
          <LoaderBar isLoading={isLoading} />
          <Card>
            <PerfectScrollbar>
              <Box minWidth={1050}>
                <Table size="small">
                  <InventoryTableHeader
                    onColumnFilterClick={onColumnFilterClick}
                  />
                  <InventoryTableBody manufacturers={manufacturers} />
                </Table>
              </Box>
            </PerfectScrollbar>
            {items?.length ? (
              <Container
                style={{
                  padding: '1em',
                  display: 'flex',
                  justifyContent: 'end'
                }}
              >
                <Pagination
                  disabled={!hasStatus}
                  count={meta?.last_page}
                  page={meta?.current_page}
                  onChange={onHandlePageChange}
                />
              </Container>
            ) : null}
          </Card>
        </Box>
      </Container>
      <InventoryColumnsFilterModal
        visible={filterVisible}
        onHandleClose={() => setFilterVisible(false)}
      />
      {selectedColumnFilter ? (
        <InventoryLocalFilterModal
          visible={selectedColumnFilter ? true : false}
          onHandleClose={() => setSelectedColumnFilter(undefined)}
          columnKey={selectedColumnFilter || 'product_name'}
        />
      ) : null}
    </Page>
  );
};

export default InventoryView;
